'use client';

/**
 * CanadaOfferingShelf — the "Available in Canada" band.
 *
 * The handoff renders a single tombstone under a standard `.cat-header`, so
 * this composes the shared SectionHeader + OfferingCard directly instead of
 * going through OfferingShelf: the Canada card needs its own disclosure copy
 * (the auto Reg D/Reg A+ line does not apply to an offering made under a
 * Canadian prospectus exemption), its own series subhead, and a "Book a Call"
 * CTA rather than "View details".
 *
 * The CTA books a call; the tombstone body itself opens the campaign page —
 * clicking the card must never drop the visitor into a calendar (Amir QA).
 */
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { styled } from '@mui/material/styles';
import { COLORS, FONTS } from 'components/HomepageV3/theme/tokens';
import SectionHeader from 'components/HomepageV3/components/SectionHeader';
import OfferingCard from 'components/HomepageV3/components/OfferingCard';
import type { Company, OfferingCardData } from 'components/HomepageV3/data/demoData';
import type useCanadaLandingPage from 'hooks/useCanadaLandingPage';
import { CANADA_OFFERING_ROUTE } from '../data/canadaSite';

type CanadaContent = ReturnType<typeof useCanadaLandingPage>;

export interface CanadaOfferingShelfProps {
  shelf: CanadaContent['shelf'];
  card: CanadaContent['card'];
  ctaLabel: string;
  /** "Book a Call" — the CTA button only. */
  onCtaClick: () => void;
  /** Tombstone-body click. Defaults to navigating to the campaign page. */
  onViewOffering?: () => void;
  className?: string;
  'data-cy'?: string;
}

const Band = styled('section')({
  background: COLORS.cream,
  padding: '48px 0 52px',
  fontFamily: FONTS.sans,
  scrollMarginTop: '84px',
});

const Head = styled('div')({
  padding: '0 var(--pad)',
});

const Track = styled('div')({
  display: 'flex',
  padding: '4px var(--pad) 14px',
});

const Cell = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

/* Secondary link under the tombstone CTA (handoff: "View offering →"). */
const ViewOfferingLink = styled(Link)({
  display: 'block',
  textAlign: 'center',
  marginTop: '10px',
  fontFamily: FONTS.sans,
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: COLORS.gold,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
});

export function CanadaOfferingShelf({
  shelf,
  card,
  ctaLabel,
  onCtaClick,
  onViewOffering,
  className,
  'data-cy': dataCy = 'canada-offering-shelf',
}: CanadaOfferingShelfProps) {
  const router = useRouter();

  const company: Company = {
    key: card.companyKey,
    mono: card.mono,
    name: card.name,
    series: card.series,
    sector: card.sector,
    invested: '',
    desc: card.desc,
    reg: 'Offered in Canada through Belco Private Capital Inc. under prospectus exemptions',
    // Gives the tombstone a real destination, so Cmd/Ctrl- and middle-click
    // open the campaign page in a background tab like any other card.
    href: CANADA_OFFERING_ROUTE,
  };

  const cardData: OfferingCardData = {
    co: card.companyKey,
    badge: { cls: 'bdg-teal', label: card.badgeLabel, dot: true },
    meta: card.metaPill,
    desc: card.desc,
    figL: { lbl: 'Minimum', val: card.minimum, cls: 'amber' },
    figR: { lbl: 'Structure', val: card.structure },
    fill: { w: card.fillPercent, cls: 'ok' },
  };

  return (
    <Band id="available-in-canada" className={className} data-cy={dataCy}>
      <Head>
        <SectionHeader
          eyebrow={shelf.eyebrow}
          title={shelf.title}
          desc={shelf.desc}
          data-cy="canada-offering-shelf-header"
        />
      </Head>
      <Track data-cy="canada-offering-shelf-track">
        <Cell>
          <OfferingCard
            card={cardData}
            company={company}
            variant="light"
            disclosure={card.disclosure}
            seriesSubhead={card.series}
            ctaLabel={ctaLabel}
            onView={onCtaClick}
            onCardActivate={onViewOffering ?? (() => router.push(CANADA_OFFERING_ROUTE))}
            data-cy="canada-offering-card"
          />
          <ViewOfferingLink href={CANADA_OFFERING_ROUTE} data-cy="canada-offering-card-view-link">
            View offering →
          </ViewOfferingLink>
        </Cell>
      </Track>
    </Band>
  );
}

export default CanadaOfferingShelf;
